import { useQuery } from "@apollo/client/react";
import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import React, { ChangeEvent, Dispatch, SetStateAction, useMemo, useState, useTransition } from "react";
import { DataLoader } from "../../../components";
import { LoadingContainer, SuccessContainer } from "../../../components/DataLoader";
import { Pill } from "../../../components/ui/Pill";
import {
  HomepageDocument,
  HomepageDownloadSummary,
  HomepageQuery,
  HomepageQueryVariables,
  HomepageUploadSummary,
} from "../../../graphql/types";

import { HOMEPAGE_QUERY } from "../../../graphql/queries/homepage";
import AccentDanger from "../../../icons/AccentDanger.svg?react";
import AccentInfo from "../../../icons/AccentInfo.svg?react";
import AccentSuccess from "../../../icons/AccentSuccess.svg?react";
import Issues from "../../../icons/Issues.svg?react";
import { useDebounce } from "../../../lib/Hooks";
import { CustomSlider, ICustomSliderProps } from "./CustomSlider";
import { Projects } from "./Projects";
import { NoItems, NoItemsOrLoadingContainer } from "./Rows";
import { Statuses } from "./Statuses";
import { EnabledStatus, EnabledType, InfoProject, InfoStatus } from "./types";
import { enabledStatusOrdinal, shouldDownloadOrUploadBeShown } from "./utils";

type ResolveResultsProps = {
  groupingValue: "status" | "project";
  infoProject: InfoProject;
  infoStatus: InfoStatus;
  selectedFilterProjectNames: string[];
  sparklineFirstDate: string;
  expandedItems: string[];
  setExpandedItems: Dispatch<SetStateAction<string[]>>;
  refreshData: () => Promise<void>;
  refreshing: boolean;
};

const ResolveResults = ({
  groupingValue,
  infoProject,
  infoStatus,
  selectedFilterProjectNames,
  sparklineFirstDate,
  expandedItems,
  setExpandedItems,
  refreshData,
  refreshing,
}: ResolveResultsProps) => {
  let filteredInfoProjects: InfoProject = {};
  if (selectedFilterProjectNames.length === 0) {
    filteredInfoProjects = infoProject;
  } else {
    for (const key in infoProject) {
      if (selectedFilterProjectNames.includes(key)) {
        filteredInfoProjects[key] = infoProject[key];
      }
    }
  }
  return (
    <>
      {groupingValue === "project" ? (
        <Projects
          infoProject={filteredInfoProjects}
          sparklineFirstDate={sparklineFirstDate}
          expandedItems={expandedItems}
          setExpandedItems={setExpandedItems}
          refreshData={refreshData}
          refreshing={refreshing}
        />
      ) : (
        <Statuses
          infoStatus={infoStatus}
          sparklineFirstDate={sparklineFirstDate}
          expandedItems={expandedItems}
          setExpandedItems={setExpandedItems}
          refreshData={refreshData}
          refreshing={refreshing}
        />
      )}
    </>
  );
};

const recomputeData = (
  enabledStatus: EnabledStatus,
  enabledType: EnabledType,
  data: HomepageQuery | undefined,
  searchValue: string
) => {
  const infoStatus: InfoStatus = {};
  const infoProject: {
    [key: string]: {
      downloads: Omit<HomepageDownloadSummary, "status" | "keys">[];
      uploads: Omit<HomepageUploadSummary, "status" | "keys" | "dependencies">[];
    };
  } = {};
  if (data?.homepage.__typename !== "Homepage") {
    return { infoStatus, infoProject };
  }

  const lowercasedSearchValue = searchValue?.toLowerCase().trim();
  if (enabledType.download) {
    data.homepage.downloads.forEach((download) => {
      if (
        searchValue &&
        !download.name.toLowerCase().includes(lowercasedSearchValue) &&
        !download.pos.path.toLowerCase().includes(lowercasedSearchValue)
      ) {
        return;
      }

      const { show, statusName } = shouldDownloadOrUploadBeShown(
        enabledStatus,
        download.sparkline,
        download.dependencyStatus
      );
      if (!show) return;

      // Populate infoStatus
      if (infoStatus[statusName]) {
        infoStatus[statusName].downloads.push(download);
      } else {
        infoStatus[statusName] = {
          downloads: [download],
          uploads: [],
        };
      }

      // Populate infoProject
      if (infoProject[download.pos.path]) {
        infoProject[download.pos.path].downloads.push(download);
      } else {
        infoProject[download.pos.path] = {
          ...infoProject[download.pos.path],
          downloads: [download],
        };
      }
    });
  }

  if (enabledType.upload) {
    data.homepage.uploads.forEach((upload) => {
      if (
        searchValue &&
        !upload.name.toLowerCase().includes(lowercasedSearchValue) &&
        !upload.pos.path.toLowerCase().includes(lowercasedSearchValue)
      ) {
        return;
      }
      const { show, statusName } = shouldDownloadOrUploadBeShown(
        enabledStatus,
        upload.sparkline,
        upload.dependencyStatus
      );
      if (!show) return;

      // Populate infoStatus
      if (infoStatus[statusName]) {
        if (infoStatus[statusName].uploads?.length) {
          infoStatus[statusName].uploads.push(upload);
        } else {
          infoStatus[statusName].uploads = [upload];
        }
      } else {
        infoStatus[statusName] = {
          uploads: [upload],
          downloads: [],
        };
      }

      // Populate infoProject
      if (infoProject[upload.pos.path]) {
        if (infoProject[upload.pos.path].uploads?.length) {
          infoProject[upload.pos.path].uploads.push(upload);
        } else {
          infoProject[upload.pos.path].uploads = [upload];
        }
      } else {
        infoProject[upload.pos.path] = {
          ...infoProject[upload.pos.path],
          uploads: [upload],
        };
      }
    });
  }

  return { infoStatus, infoProject };
};

const DataUnderManagement = () => {
  const [isPending, startTransition] = useTransition();
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchText, 500);

  const { loading, error, data, refetch, client } = useQuery<HomepageQuery, HomepageQueryVariables>(HomepageDocument, {
    variables: {
      refresh: false,
    },
  });

  const noItemsMessage =
    data?.homepage.__typename === "NoPublishedScriptbook"
      ? "No scriptbook has been published. Publish some pipelines"
      : data?.homepage.__typename === "Homepage" &&
          data.homepage.downloads.length === 0 &&
          data.homepage.uploads.length === 0
        ? "The published scriptbook is empty. There are no running pipelines. Publish some pipelines"
        : null;

  const refreshData = async () => {
    setRefreshing(true);
    const { data: newData } = await refetch({ refresh: true });
    client.writeQuery({
      query: HOMEPAGE_QUERY,
      data: newData,
      variables: {
        refresh: false,
      },
    });
    setRefreshing(false);
  };

  const [groupingValue, setGroupingValue] = useState<"status" | "project">("status");
  const [expandAll, setExpandAll] = useState(false);
  const [selectedFilterProjectNames, setSelectedFilterProjectNames] = useState<string[]>([]);

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [enabledStatus, setEnabledStatus] = useState<EnabledStatus>({
    error: true,
    warning: false,
    issue: false,
    working: false,
  });
  const [enabledType, setEnabledType] = useState<EnabledType>({
    download: true,
    upload: true,
  });
  const theme = useTheme();

  const { infoProject, infoStatus } = useMemo(() => {
    if (data?.homepage.__typename === "Homepage") {
      return recomputeData(enabledStatus, enabledType, data, debouncedSearchValue);
    }
    return { infoProject: {}, infoStatus: {} };
  }, [enabledStatus, enabledType, data, debouncedSearchValue]);

  const showCollapseExpandButton = useMemo(() => {
    if (groupingValue === "status") return Object.keys(infoStatus).length > 0;
    return Object.keys(infoProject).length > 0;
  }, [groupingValue, infoStatus, infoProject]);

  const handleChange = (event: React.MouseEvent<HTMLElement>, newgroupingValue: "status" | "project") => {
    if (newgroupingValue === null) {
      return;
    }
    startTransition(() => {
      setExpandedItems([]);
      setExpandAll(false);
      setGroupingValue(newgroupingValue);
    });
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const isClickedStatusMoreImportantThanEnabled = (clickedStatus: number) => {
    return enabledStatusOrdinal(enabledStatus) < clickedStatus;
  };

  const onFilterClick = (newStatus: EnabledStatus, newEnabledType?: EnabledType) => {
    startTransition(() => {
      setEnabledStatus(newStatus);
      if (newEnabledType) setEnabledType(newEnabledType);
      setSelectedFilterProjectNames([]);
      if (expandAll) {
        const { infoProject: newInfoProject, infoStatus: newInfoStatus } = recomputeData(
          newStatus,
          newEnabledType || enabledType,
          data,
          debouncedSearchValue
        );
        setExpandedItems(Object.keys(groupingValue === "status" ? newInfoStatus : newInfoProject));
      }
    });
  };

  const sliderOptions: ICustomSliderProps["options"] = [
    {
      label: "Error",
      onClick: () => {
        const newStatus = {
          error: true,
          issue: false,
          warning: false,
          working: false,
        };
        onFilterClick(newStatus);
      },
      leftIcon: (
        <AccentDanger
          style={{
            marginLeft: 0,
            marginRight: 0,
          }}
          fill={theme.palette.accentDanger30.main}
          color={theme.palette.text10.main}
          width="18px"
          height="18px"
        />
      ),
      selected: enabledStatus.error,
    },
    {
      label: "Issues",
      onClick: () => {
        const isGreatedThanClicked = isClickedStatusMoreImportantThanEnabled(2);
        const newStatus = {
          error: true,
          issue: isGreatedThanClicked ? true : !enabledStatus.issue,
          warning: false,
          working: false,
        };
        onFilterClick(newStatus);
      },
      leftIcon: (
        <Issues
          style={{
            marginLeft: 0,
            marginRight: 0,
          }}
          fill={theme.palette.accentWarning30.main}
          color={theme.palette.text10.main}
          width="18px"
          height="18px"
        />
      ),
      selected: enabledStatus.issue,
    },
    {
      label: "Notice",
      onClick: () => {
        const isGreatedThanClicked = isClickedStatusMoreImportantThanEnabled(1);
        const newStatus = {
          error: true,
          issue: true,
          warning: isGreatedThanClicked ? true : !enabledStatus.warning,
          working: false,
        };
        onFilterClick(newStatus);
      },
      leftIcon: (
        <AccentInfo
          style={{
            marginLeft: 0,
            marginRight: 0,
          }}
          fill={theme.palette.accentInfo30.main}
          color={theme.palette.text10.main}
          width="18px"
          height="18px"
        />
      ),
      selected: enabledStatus.warning,
    },
    {
      label: "Working",
      onClick: () => {
        const newStatus = {
          error: true,
          issue: true,
          warning: true,
          working: !enabledStatus.working,
        };
        onFilterClick(newStatus);
      },
      leftIcon: (
        <AccentSuccess
          style={{
            marginLeft: 0,
            marginRight: 0,
          }}
          fill={theme.palette.accentSuccess30.main}
          color={theme.palette.text10.main}
          width="18px"
          height="18px"
        />
      ),
      selected: enabledStatus.working,
    },
  ];

  return (
    <>
      <Typography sx={{ color: "text50.main" }} variant="heading.four">
        Data under management
      </Typography>
      <Stack>
        <Stack sx={{ gap: "16px" }}>
          <Stack direction="row" sx={{ justifyContent: "space-between" }}>
            <Stack sx={{ gap: "4px" }}>
              <Typography sx={{ color: "text40.main" }} variant="body.medium.semibold">
                Group by
              </Typography>
              <Stack direction="row">
                <ToggleButtonGroup value={groupingValue} exclusive onChange={handleChange} color="secondary">
                  <ToggleButton
                    value="status"
                    aria-label="status"
                    sx={{ marginRight: "-8px", width: "134px", height: "44px" }}
                  >
                    Status
                  </ToggleButton>
                  <ToggleButton
                    sx={{
                      width: "134px",
                      height: "44px",
                      marginLeft: "8px",
                    }}
                    value="project"
                    aria-label="project"
                  >
                    Project
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            <TextField
              sx={{
                backgroundColor: "surfaceLight10.main",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                },
                borderRadius: "4px",
                height: "fit-content",
                marginTop: "auto",
              }}
              placeholder="Search by project, download, or upload name..."
              variant="outlined"
              value={searchText}
              onChange={handleSearch}
              slotProps={{
                input: {
                  endAdornment: <MagnifyingGlassIcon size={20} />,
                  sx: {
                    width: "400px",
                    height: "45px",
                  },
                },
              }}
            />
          </Stack>

          <Stack direction="row">
            <Stack sx={{ gap: "4px" }}>
              <Typography sx={{ color: "text40.main" }} variant="body.medium.semibold">
                Filter by
              </Typography>
              <Stack direction="row" sx={{ height: "26px" }}>
                <CustomSlider options={sliderOptions} />
              </Stack>
            </Stack>
            <Stack sx={{ ml: "60px", width: "300px", gap: "4px" }}>
              <Typography sx={{ color: "text40.main" }} variant="body.medium.semibold">
                Type
              </Typography>
              <Stack direction="row" sx={{ gap: "12px" }}>
                <Pill
                  selected={enabledType.download}
                  label="Downloads"
                  icon={<CloudArrowDownIcon size="18px" color={theme.palette.text50.main} />}
                  deleteIcon={
                    <CheckIcon
                      color={theme.palette.text50.main}
                      style={{
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  }
                  onClick={() => {
                    const newEnabledType = {
                      ...enabledType,
                      download: !enabledType.download,
                    };
                    onFilterClick(enabledStatus, newEnabledType);
                  }}
                  onDelete={enabledType.download ? () => {} : undefined}
                />
                <Pill
                  selected={enabledType.upload}
                  label="Uploads"
                  icon={<CloudArrowUpIcon size="18px" />}
                  deleteIcon={
                    <CheckIcon
                      color={theme.palette.text50.main}
                      style={{
                        width: "18px",
                        height: "18px",
                      }}
                    />
                  }
                  onClick={() => {
                    const newEnabledType = {
                      ...enabledType,
                      upload: !enabledType.upload,
                    };
                    onFilterClick(enabledStatus, newEnabledType);
                  }}
                  onDelete={enabledType.upload ? () => {} : undefined}
                />
              </Stack>
            </Stack>

            {showCollapseExpandButton && (
              <Stack
                direction="row"
                sx={{ alignItems: "flex-end", justifyContent: "flex-end", width: "150px", ml: "auto" }}
              >
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => {
                    startTransition(() => {
                      setExpandAll(!expandAll);
                      if (!infoProject) return;
                      if (expandAll) {
                        setExpandedItems([]);
                      } else {
                        setExpandedItems(Object.keys(groupingValue === "status" ? infoStatus : infoProject));
                      }
                    });
                  }}
                >
                  {expandAll ? "Collapse tables" : "Expand tables"}
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack>
        <DataLoader
          loading={loading || isPending}
          error={error}
          success={(infoProject && groupingValue === "project") || (infoStatus && groupingValue === "status")}
          defaults={{ small: false }}
        >
          <LoadingContainer>
            <NoItemsOrLoadingContainer>
              <CircularProgress
                size="24px"
                thickness={4}
                sx={{
                  color: theme.palette.text50.main,
                }}
              />
            </NoItemsOrLoadingContainer>
          </LoadingContainer>
          <SuccessContainer>
            {noItemsMessage ? (
              <NoItems message={noItemsMessage} />
            ) : (
              <ResolveResults
                groupingValue={groupingValue}
                infoProject={infoProject}
                infoStatus={infoStatus}
                selectedFilterProjectNames={selectedFilterProjectNames}
                sparklineFirstDate={data?.homepage.__typename === "Homepage" ? data.homepage.sparklineFirstDate : ""}
                expandedItems={expandedItems}
                setExpandedItems={setExpandedItems}
                refreshData={refreshData}
                refreshing={refreshing}
              />
            )}
          </SuccessContainer>
        </DataLoader>
      </Stack>
    </>
  );
};

export { DataUnderManagement };
